import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { 
  UserQuota
} from '../utils/firestoreApi';
import { getCachedSubscription, clearUserCache } from '../utils/subscriptionCache';
import { 
  initializeQuotaCache, 
  checkQuotaLimitLocal,
  incrementQuotaLocal,
  clearUserQuotaCache
} from '../utils/quotaCache';
import { UsageType, getMaxKey } from '../utils/quotaConfig';
import { PricingTier } from '../types';






interface QuotaState {
  quota: UserQuota | null;
  tier: PricingTier;
  loading: boolean;
  error: string | null;
}

interface QuotaHookReturn extends QuotaState {
  checkLimit: (usageType: UsageType) => Promise<boolean>;
  incrementAndCheck: (usageType: UsageType) => Promise<boolean>;
  refreshQuota: () => Promise<void>;
  hasUnlimitedAccess: (usageType: UsageType) => boolean;
  getUsagePercentage: (usageType: UsageType) => number;
}

export const useQuota = (): QuotaHookReturn => {
  const { user } = useAuth();
  const [state, setState] = useState<QuotaState>({
    quota: null,
    tier: PricingTier.Free,
    loading: true,
    error: null
  });

  const refreshQuota = useCallback(async () => {
    if (!user?.uid) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Get user's current subscription to determine tier (using cache)
      const subscription = await getCachedSubscription(user.uid);
      const tier = subscription.tier;

      // Initialize quota cache with subscription tier (this will create default quotas if needed)
      const quota = await initializeQuotaCache(user.uid, tier);

      setState({
        quota,
        tier,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching quota:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch quota'
      }));
    }
  }, [user?.uid]);

  useEffect(() => {
    refreshQuota();
    
    // Cleanup function to clear cache when user changes
    return () => {
      if (user?.uid) {
        clearUserCache(user.uid);
        clearUserQuotaCache(user.uid);
      }
    };
  }, [refreshQuota, user?.uid]);

  // Listen for quota update events from other parts of the app
  useEffect(() => {
    const handleQuotaUpdate = () => {
      refreshQuota();
    };

    const handleSubscriptionUpdate = () => {
      refreshQuota();
    };

    window.addEventListener('quota-updated', handleQuotaUpdate);
    window.addEventListener('subscription-updated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('quota-updated', handleQuotaUpdate);
      window.removeEventListener('subscription-updated', handleSubscriptionUpdate);
    };
  }, [refreshQuota]);

  const checkLimit = useCallback(async (usageType: UsageType): Promise<boolean> => {
    if (!user?.uid) {
      return false; // Allow usage if we can't check
    }
    
    try {
      // Use local quota cache for fast checking
      const result = await checkQuotaLimitLocal(user.uid, usageType);
      return result;
    } catch (error) {
      console.error(`❌ [useQuota] Error checking limit for ${usageType}:`, error);
      return false; // Allow usage on error
    }
  }, [user?.uid]);

  const incrementAndCheck = useCallback(async (usageType: UsageType): Promise<boolean> => {
    if (!user?.uid) return false;

    try {
      // Check if user has reached limit before incrementing (using local cache)
      const hasReachedLimit = await checkQuotaLimitLocal(user.uid, usageType);
      if (hasReachedLimit) {
        return false;
      }

      // Increment quota locally (fast) and queue cloud update
      await incrementQuotaLocal(user.uid, usageType);
      
      // Refresh local quota state to reflect changes
      await refreshQuota();
      
      return true;
    } catch (error) {
      console.error('Error incrementing usage:', error);
      return false;
    }
  }, [user?.uid, refreshQuota]);

  const hasUnlimitedAccess = useCallback((usageType: UsageType): boolean => {
    if (!state.quota) return false;
    
    const maxKey = getMaxKey(usageType);
    return state.quota[maxKey] === -1;
  }, [state.quota]);

  const getUsagePercentage = useCallback((usageType: UsageType): number => {
    if (!state.quota) return 0;
    
    const maxKey = getMaxKey(usageType);
    const maxValue = state.quota[maxKey];
    const usedValue = state.quota[usageType];
    
    if (maxValue === -1) return 0; // Unlimited
    if (maxValue === 0) return 100; // No quota
    
    return Math.min((usedValue / maxValue) * 100, 100);
  }, [state.quota]);

  return {
    ...state,
    checkLimit,
    incrementAndCheck,
    refreshQuota,
    hasUnlimitedAccess,
    getUsagePercentage
  };
};


// Hook specifically for checking if a feature is available
export const useFeatureAccess = () => {
  const { quota, tier, hasUnlimitedAccess, checkLimit, getUsagePercentage } = useQuota();
  const { user } = useAuth();
  
  const canCreateThreadStarter = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('threadStarters')) {
      return true;
    }
    
    const result = !(await checkLimit('threadStarters'));
    return result;
  }, [hasUnlimitedAccess, checkLimit]);
  
  const canCreateThreadIdea = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('totalThreads')) {
      return true;
    }
    return !(await checkLimit('totalThreads'));
  }, [hasUnlimitedAccess, checkLimit]);
  
  const canCreateTotalNote = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('totalThreads')) return true;
    return !(await checkLimit('totalThreads'));
  }, [hasUnlimitedAccess, checkLimit]);
  
  const canUploadFile = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('fileAttachments')) return true;
    return !(await checkLimit('fileAttachments'));
  }, [hasUnlimitedAccess, checkLimit]);
  
  const canPerformMerge = useCallback(async (): Promise<boolean> => {
    // Merge operations are now part of totalThreads quota
    if (hasUnlimitedAccess('totalThreads')) return true;
    return !(await checkLimit('totalThreads'));
  }, [hasUnlimitedAccess, checkLimit]);
  
  const canPerformWebSearch = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('webSearches')) return true;
    return !(await checkLimit('webSearches'));
  }, [hasUnlimitedAccess, checkLimit]);
  
  const canSendConversationalMessage = async (): Promise<boolean> => {
    // Conversational messages are now unlimited
    return true;
  };

  const canSendChatMessage = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('monthlyChatMessages')) {
      return true;
    }
    
    const result = !(await checkLimit('monthlyChatMessages'));
    return result;
  }, [hasUnlimitedAccess, checkLimit]);

  const canCreateCanvas = useCallback(async (): Promise<boolean> => {
    if (hasUnlimitedAccess('totalCanvases')) {
      return true;
    }

    if (!user?.uid) {
      return false;
    }

    try {
      const hasExceeded = await checkLimit('totalCanvases');
      const result = !hasExceeded;
      return result;
    } catch (error) {
      console.error('❌ [useFeatureAccess] Error checking canvas creation quota:', error);
      return false;
    }
  }, [hasUnlimitedAccess, user?.uid, checkLimit]);

  return {
    quota,
    tier,
    canCreateThreadStarter,
    canCreateThreadIdea,
    canCreateTotalNote,
    canUploadFile,
    canPerformMerge,
    canPerformWebSearch,
    canSendConversationalMessage,
    canSendChatMessage,
    canCreateCanvas,
    getUsagePercentage
  };
};